import React from 'react';
import { Building2, ChevronRight, Briefcase, Users, Clock, AlertCircle, CheckCircle2 } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * ClientCard - Composant pour suivi et décision rapide sur les clients
 * 
 * Props:
 * - client: objet client avec toutes les données enrichies
 * - onViewMissions: callback pour voir les missions du client
 * - onViewDetail: callback pour voir le détail du client
 */
const ClientCard = ({ 
  client, 
  onViewMissions, 
  onViewDetail
}) => {
  
  // === STATUS DISPLAY ===
  const getStatusDisplay = () => {
    const missionsOpen = client.missions_open || 0;
    const missionsActive = client.missions_active || 0;
    const daysInactive = client.days_inactive;
    
    // Client avec missions à staffer (priorité)
    if (missionsOpen > 0) {
      return { 
        label: 'ACTION REQUISE', 
        color: 'bg-orange-500', 
        textColor: 'text-white',
        icon: AlertCircle
      };
    }
    
    // Client avec missions en cours
    if (missionsActive > 0) {
      return { 
        label: 'ACTIF', 
        color: 'bg-green-500', 
        textColor: 'text-white',
        icon: CheckCircle2
      };
    }
    
    // Client inactif depuis longtemps
    if (daysInactive && daysInactive > 30) {
      return { 
        label: 'INACTIF', 
        color: 'bg-gray-300', 
        textColor: 'text-gray-700',
        icon: Clock
      };
    }
    
    // Client sans missions
    return { 
      label: 'PROSPECT', 
      color: 'bg-blue-100', 
      textColor: 'text-blue-700',
      icon: Building2
    };
  };

  // === INFO SIGNAL ===
  const getInfoSignal = () => {
    const missionsOpen = client.missions_open || 0;
    const missionsActive = client.missions_active || 0;
    const daysInactive = client.days_inactive;
    
    if (missionsOpen > 0) {
      return { 
        icon: Briefcase, 
        label: `${missionsOpen} mission${missionsOpen > 1 ? 's' : ''} à staffer`, 
        color: 'text-orange-600' 
      };
    }
    
    if (missionsActive > 0) {
      return { 
        icon: Briefcase, 
        label: `${missionsActive} mission${missionsActive > 1 ? 's' : ''} en cours`, 
        color: 'text-green-600' 
      };
    }
    
    if (daysInactive && daysInactive > 30) {
      return { 
        icon: Clock, 
        label: `Inactif depuis ${daysInactive}j`, 
        color: 'text-gray-400' 
      };
    }
    
    return null;
  };

  // === TIME SIGNAL ===
  const getTimeSignal = () => {
    const created = new Date(client.created_at);
    const now = new Date();
    const diffMs = now - created;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Aujourd\'hui';
    if (diffDays === 1) return 'Hier';
    if (diffDays < 7) return `il y a ${diffDays}j`;
    if (diffDays < 30) return `il y a ${Math.floor(diffDays / 7)} sem.`;
    return `il y a ${Math.floor(diffDays / 30)} mois`;
  };

  // === COMPUTED VALUES ===
  const statusDisplay = getStatusDisplay();
  const infoSignal = getInfoSignal();
  const timeSignal = getTimeSignal();
  const StatusIcon = statusDisplay.icon;

  const missionsTotal = client.missions_total || 0;
  const candidatesProposed = client.candidates_proposed || 0;

  return (
    <div 
      className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
      data-testid={`client-card-${client.id}`}
    >
      {/* === LIGNE 1: STATUT === */}
      <div className="px-4 pt-4 pb-2 flex items-center justify-between">
        <span className={cn(
          "inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-bold tracking-wide",
          statusDisplay.color, statusDisplay.textColor
        )}>
          <StatusIcon className="h-3.5 w-3.5" />
          {statusDisplay.label}
        </span>
        <span className="text-xs text-gray-400">{timeSignal}</span>
      </div>

      {/* === LIGNE 2: SIGNAL INFO === */}
      {infoSignal && (
        <div className="px-4 pb-2">
          <span className={cn("flex items-center gap-1.5 text-xs font-medium", infoSignal.color)}>
            <infoSignal.icon className="h-3.5 w-3.5" />
            {infoSignal.label}
          </span>
        </div>
      )}

      {/* === SÉPARATEUR === */}
      <div className="border-t border-gray-100 mx-4" />

      {/* === ZONE IDENTITÉ CLIENT === */}
      <div className="px-4 py-3">
        <h3 className="text-lg font-semibold text-gray-900">
          {client.company_name}
        </h3>
        <p className="text-sm text-gray-500">
          {client.contact_name} • {client.industry || 'Secteur non défini'}
        </p>
      </div>

      {/* === ZONE MÉTRIQUES === */}
      <div className="px-4 pb-3 flex items-center gap-4 text-sm">
        <span className="flex items-center gap-1.5 text-gray-600">
          <Briefcase className="h-4 w-4 text-gray-400" />
          {missionsTotal} mission{missionsTotal !== 1 ? 's' : ''}
        </span>
        {candidatesProposed > 0 && (
          <span className="flex items-center gap-1.5 text-gray-600">
            <Users className="h-4 w-4 text-gray-400" />
            {candidatesProposed} proposé{candidatesProposed !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* === SÉPARATEUR === */}
      <div className="border-t border-gray-100 mx-4" />

      {/* === ZONE ACTIONS === */}
      <div className="p-4 flex gap-3">
        {missionsTotal > 0 && (
          <button
            onClick={onViewMissions}
            className="flex-1 flex items-center justify-center gap-2 bg-tp-primary hover:bg-tp-primary-dark text-white font-medium py-3 px-4 rounded-lg transition-colors"
            data-testid={`view-missions-${client.id}`}
          >
            <Briefcase className="h-4 w-4" />
            Voir missions
          </button>
        )}
        <button
          onClick={onViewDetail}
          className={cn(
            "flex items-center justify-center gap-2 font-medium py-3 px-4 rounded-lg transition-colors",
            missionsTotal > 0
              ? "flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700"
              : "flex-1 bg-tp-primary hover:bg-tp-primary-dark text-white"
          )}
          data-testid={`view-detail-${client.id}`}
        >
          Voir détail
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default ClientCard;
